import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IKuikaDataType,
  ISymbol
} from "@kuika/kuika-cl-model";
import { IUserInteraction, IWFParameter } from "@kuika/kuika-cl-model/src/models/workflow";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { getDefaultDesignerActionSymbol } from "../../../../shared/utility/designer-action-actions";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { getWorkflowParamTypeLabel } from "../../left-panel/work-flow-list/workflow-parameter-type-options";
import { EActionPanelStatus, IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { ActionSymbolWithChild } from "./action-symbol-with-child";
import { SymbolPicker } from "./symbol-picker";

export interface IWFResumeSymbolsGroupState {}

export interface IWFResumeSymbolsGroupProps {
  action?: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  updateParams: (parameters: IActionParameter[]) => void;
  userInteractions: IUserInteraction[];
  processParameters: IWFParameter[];
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
  updateAddGoBackAction: (value: boolean) => void;
  addGoBackActionValue?: boolean;
}

class WFResumeSymbolsGroup extends PureSdComponent<
  IWFResumeSymbolsGroupProps & IWithSDProps & IWithContextProp,
  IWFResumeSymbolsGroupState
> {
  constructor(props: IWFResumeSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  componentDidMount(): void {
    this.renderParameterMappings(true);
    this.renderParameterMappings(false);
  }

  componentDidUpdate(prevProps: IWFResumeSymbolsGroupProps): void {
    if (prevProps.parameters !== this.props.parameters) {
      this.renderParameterMappings(true);
      this.renderParameterMappings(false);
    }
  }

  getStepOutputValue = (name) => {
    return this.props.parameters.find((p) => p.name == name)?.symbol;
  };

  deleteParameter = (parameterName: string) => {
    const actionParameters = _.clone(this.props.parameters);
    const newActionParameters = actionParameters.filter((p) => p.name !== parameterName);
    this.props.updateParams(newActionParameters);
  };

  getParameterWithName = (name: string): IActionParameter | undefined => {
    return this.props.parameters.find((x) => x.name === name);
  };

  outcomeMessageNeedToBeRendered = (): boolean => {
    const outComeMessage = this.getParameterWithName("OutcomeMessage");
    if (!outComeMessage) return false;
    if (outComeMessage.isOptionalParameter === false) return true;
    if (outComeMessage.symbol.fixedValue) return true;
    return outComeMessage.isExternalVisible === true;
  };

  renderOutComeMessage = () => {
    const outComeMessage = this.getParameterWithName("OutcomeMessage");
    if (!outComeMessage) return null;

    if (!this.outcomeMessageNeedToBeRendered()) {
      return null;
    }

    return (
      <div key={outComeMessage.name}>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          <ActionParameterTitle
            parameter={{
              name: outComeMessage.name,
              Typename: outComeMessage.Typename,
              cardinality: outComeMessage.cardinality
            }}
          />
        </span>
        {
          <SymbolPicker
            action={this.props.action}
            changeType="onBlur"
            symbol={outComeMessage.symbol}
            valueType={outComeMessage.Typename}
            targetCardinality={outComeMessage.cardinality}
            onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
              this.props.changeActionParameter(outComeMessage, symbol, changeReason)
            }
            bindingParameter={outComeMessage}
          ></SymbolPicker>
        }
      </div>
    );
  };

  renderParameterMappings = (isBusinessObject: boolean) => {
    if (this.props.parameters && this.props.parameters.filter((p) => p.name !== "workflowStepInstanceId").length > 0) {
      return this.props.parameters
        .filter((p) => p.name !== "workflowStepInstanceId")
        .filter((p) => !(p.isOptionalParameter === true && p.isExternalVisible === false))
        .filter((p) => p.name !== "userInteractionId")
        .filter((p) => p.name !== "isCreatedByWizard")
        .filter(
          (p) => this.props.processParameters.find((x) => x.name === p.name)?.isBusinessObject === isBusinessObject
        )
        .map((p) => {
          const wfParameter = this.props.processParameters.find((x) => x.name === p.name);
          const userInteraction = this.props.userInteractions.find(
            (x) => x.id === this.getParameterWithName("userInteractionId")?.symbol.fixedValue
          );
          const interactionParameter = userInteraction?.parameters.find(
            (x) => wfParameter?.id === x.id && x.isMailParameter !== true
          );
          // Codegen (Analyser) applies interaction values to INTERNAL params only
          // (!wfParam.IsBusinessObject) — a BO param always resumes with the action's own symbol.
          // Mirror that here: an internal param defined on the interaction mirrors the interaction's
          // value and is locked below; a BO param keeps its own mapping and stays user-editable (the
          // interaction value is only copied in as a seed when the user picks the interaction).
          if (interactionParameter && !isBusinessObject) {
            p.symbol = { ...interactionParameter?.value } as ISymbol;
          }

          // An object param (single OR list) uses the shared ActionSymbolWithChild — it renders the
          // picker, the List<x>/type label, and the inner field-mapping drawer, and persists (incl.
          // nested fields) through the nested-aware changeActionParameter. Its nested `parameters` are
          // expanded from the referenced Kuika Data Type so the drawer has fields to map. The client
          // codegen (GenerateNestedParameterClientCode) already assembles both single and list objects.
          const useObjectDrawer: boolean = wfParameter?.isObject === true;
          if (useObjectDrawer) {
            p.isObject = true;
            p.Typename = (wfParameter?.dataTypeName ?? p.Typename) as BackEndValueType;
            p.cardinality = wfParameter?.cardinality ?? ECardinality.One;
            // objectClassName is the emitted Kuika Data Type POCO the workflow-request codegen news up
            // (`new {ObjectClassName}()` / `new List<{ObjectClassName}>()`); it resolves in the generated
            // controller's Kuika.{Domain}.Api namespace, same as a datasource DTO.
            p.objectClassName = wfParameter?.dataTypeName;
            // Always rebuild the nested structure from the catalog (authoritative isObject / type),
            // seeding symbols from any existing mappings — so stale persisted params (e.g. a scalar
            // field previously flagged isObject) are corrected instead of skipped.
            p.parameters = this.catalogTypeToActionParameters(wfParameter?.dataTypeName, p.parameters, p.name);
            return (
              <div className={p.isDeletedOnSource === true ? "isDeletedOnSource" : ""} key={p.name}>
                <ActionSymbolWithChild
                  parameter={p}
                  action={this.props.action!}
                  actionDefinition={this.getResumeActionDefinition()!}
                  actionPanelStatus={EActionPanelStatus.new}
                  onActionParameterSelected={(param, symbol, changeReason) =>
                    this.props.changeActionParameter(param, symbol, changeReason)
                  }
                />
                {p.isDeletedOnSource === true && (
                  <div className="kuika_styling_typography_footer" style={{ padding: "4px 6px", display: "flow-root" }}>
                    <div style={{ float: "right" }} onClick={() => this.deleteParameter(p.name)}>
                      Delete
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // The picker's target type must come from the CURRENT workflow parameter, not the stored
          // action param: `p.Typename` is whatever this button carried when it was generated, so a
          // retyped parameter (e.g. object → Int32) leaves it stale on a per-button basis — and the
          // picker then filters every category against a type that no longer exists (Components,
          // Fixed Value, everything vanishes). The stored Typename is only the fallback when the
          // parameter no longer exists on the process. (This branch is scalar-only; the object
          // drawer returned above.)
          const resumeTargetTypeName: string = wfParameter ? String(wfParameter.dataType) : p.Typename.toString();
          const resumeTargetCardinality: ECardinality = wfParameter?.cardinality ?? ECardinality.One;
          return (
            <div className={p.isDeletedOnSource === true ? "isDeletedOnSource" : ""} key={p.name}>
              <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                {p.name} ({wfParameter ? getWorkflowParamTypeLabel(wfParameter) : p.Typename.toString()})
              </span>

              <div
                style={{
                  padding: "8px",
                  borderRadius: "var(--radius-l)",
                  background: "var(--dark-0)"
                }}
              >
                <SymbolPicker
                  action={this.props.action}
                  changeType="onBlur"
                  symbol={p.symbol}
                  valueType={resumeTargetTypeName as BackEndValueType}
                  targetCardinality={resumeTargetCardinality}
                  isObject={wfParameter?.isObject ?? false}
                  hideWFParams={true}
                  onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) => {
                    this.updateOutputParameterMapping(p, symbol, changeReason);
                  }}
                  disabled={
                    !isBusinessObject &&
                    interactionParameter !== undefined &&
                    interactionParameter.isMailParameter !== true
                  }
                  bindingParameter={p}
                />
                {p.isDeletedOnSource === true && (
                  <div className="kuika_styling_typography_footer" style={{ padding: "4px 6px", display: "flow-root" }}>
                    {
                      <div style={{ float: "right" }} onClick={() => this.deleteParameter(p.name)}>
                        Delete
                      </div>
                    }
                  </div>
                )}
              </div>
            </div>
          );
        });
    }
    return (
      <div className="kuika_styling_typography_wrapper kuika_screen-input__wrapper">
        No Output mapped to Process params
      </div>
    );
  };

  updateOutputParameterMapping = (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const actionParameters = [...this.props.parameters];
    const actionParameter = actionParameters.find((p) => p.name === parameter.name);
    if (actionParameter) {
      actionParameter.symbol = symbol;
    } else {
      actionParameters.push({
        name: parameter.name,
        symbol,
        cardinality: ECardinality.One,
        Typename: parameter.Typename
      });
    }
    this.updateValues({ actionParameters });
  };

  // The ResumeProcess action's definition (for ActionSymbolWithChild). Resolved like other panels.
  getResumeActionDefinition = (): IActionDefinitionDto | undefined =>
    this.props.sd.actionDefinitions?.find((ad) => ad.id === this.props.action?.actionDefinitionId);

  // Expands a Kuika Data Type (by name) into nested IActionParameter[] so the object drawer of
  // ActionSymbolWithChild can render a picker per field. Recurses one level per referenced object type.
  catalogTypeToActionParameters = (
    dataTypeName?: string,
    existing?: IActionParameter[],
    ownerName: string = "",
    depth: number = 0
  ): IActionParameter[] => {
    if (!dataTypeName || depth > 5) return [];
    const catalogType = (this.props.sd.types ?? []).find((t) => t.name === dataTypeName) as unknown as
      | IKuikaDataType
      | undefined;
    return (catalogType?.elements ?? []).map((f): IActionParameter => {
      const referencedType = (this.props.sd.types ?? []).find((t) => t.name === f.type) as unknown as
        | IKuikaDataType
        | undefined;
      // Only a type that actually has nested fields is an object. A scalar (e.g. "String") may still
      // resolve to a bare type entry in sd.types, so guard on elements to avoid a false drawer carrot.
      const isObjectField: boolean = !f.isList && (referencedType?.elements?.length ?? 0) > 0;
      // Datasource nested-param convention (what the workflow-request codegen requires): `name` is the
      // parent-qualified path so it is unique in the flattened request DTO; `realPropName` is the bare
      // property used to assemble the object; `displayName` is the bare label; `objectClassName` is the
      // POCO an object field is new'd as. This mirrors how KCompilerProxy shapes C# datasource params.
      const qualifiedName: string = ownerName ? `${ownerName}_${f.name}` : f.name;
      const existingField = existing?.find((e) => e.name === qualifiedName);
      const param: IActionParameter = {
        name: qualifiedName,
        realPropName: f.name,
        displayName: f.name,
        ownerPropName: ownerName || undefined,
        ownerClassName: dataTypeName,
        objectClassName: isObjectField ? f.type : undefined,
        Typename: f.type as BackEndValueType,
        cardinality: f.isList ? ECardinality.Many : ECardinality.One,
        symbol: existingField?.symbol ?? getDefaultDesignerActionSymbol(f.type),
        isObject: isObjectField || undefined
      };
      if (isObjectField)
        param.parameters = this.catalogTypeToActionParameters(
          f.type,
          existingField?.parameters,
          qualifiedName,
          depth + 1
        );
      return param;
    });
  };

  updateValues = (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => {
    if (this.props.updateValues !== undefined) {
      this.props.updateValues(value, changeReason);
    }
  };

  getUserInteractionList = (): KuikaSelectboxOption[] => {
    const userInteractions: KuikaSelectboxOption[] = [
      {
        label: "None",
        value: ""
      }
    ];
    this.props.userInteractions.map((ui: IUserInteraction) => {
      userInteractions.push({
        key: ui.id,
        label: ui.label,
        value: ui.id
      });
    });
    return userInteractions;
  };

  render() {
    return (
      <div style={{ width: "100%" }}>
        {this.getParameterWithName("isCreatedByWizard")?.symbol.fixedValue === true &&
          this.getParameterWithName("userInteractionId")?.symbol.fixedValue !== undefined && (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div className="kuika_properties_label_text">Add User Interaction</div>
              <div>
                <KuikaSwitch checked={true} className="property__switch" disabled={true} labelPosition="none" />
              </div>
            </div>
          )}

        <div>
          <label className="kuika_properties_label_text">User Interaction</label>
          <div>
            <KuikaSelectbox
              style={{ width: "100%" }}
              searchable
              placeholder="Select a user Interaction"
              allowClear={true}
              onChange={(value: string | null): void => {
                const parameters = [...this.props.parameters];
                const userInteractionId = parameters.find((x) => x.name === "userInteractionId");
                // The kit reports a cleared single select as `null`; antd reported `undefined`, and
                // a `fixedValue` of `null` would be serialised into the screen where nothing was
                // written before. `undefined` keeps the key out of the JSON, as it was.
                const fixedValue: string | undefined = value ?? undefined;
                if (userInteractionId) {
                  userInteractionId.symbol = {
                    fixedValue,
                    currentType: "String",
                    targetType: "String"
                  } as ISymbol;

                  // Update the parameters with the values from the user interaction
                  const userInteraction = this.props.userInteractions.find(
                    (x) => x.id === this.getParameterWithName("userInteractionId")?.symbol.fixedValue
                  );

                  parameters.forEach((p) => {
                    const wfParameter = this.props.processParameters.find((x) => x.name === p.name);
                    const interactionParameter = userInteraction?.parameters.find(
                      (x) => wfParameter?.id === x.id && x.isMailParameter !== true
                    );
                    if (interactionParameter) {
                      p.symbol = { ...interactionParameter?.value } as ISymbol;
                    }
                  });

                  this.updateValues({ actionParameters: parameters });
                } else {
                  parameters.push({
                    name: "userInteractionId",
                    symbol: {
                      fixedValue,
                      currentType: "String",
                      targetType: "String"
                    } as ISymbol,
                    cardinality: ECardinality.One,
                    Typename: "String"
                  });

                  this.updateValues({ actionParameters: parameters });
                }
              }}
              options={this.getUserInteractionList()}
              value={this.getParameterWithName("userInteractionId")?.symbol.fixedValue ?? null}
              disabled={this.getParameterWithName("isCreatedByWizard")?.symbol.fixedValue === true}
            />
          </div>
        </div>
        {this.props.parameters
          .filter((p) => p.name !== "workflowStepInstanceId")
          .filter((p) => !(p.isOptionalParameter === true && p.isExternalVisible === false))
          .filter((p) => p.name !== "userInteractionId")
          .filter((p) => p.name !== "isCreatedByWizard")
          .filter((p) => this.props.processParameters.find((x) => x.name === p.name)?.isBusinessObject === true)
          .length > 0 && (
          <div style={{ marginTop: "8px", fontWeight: "600", fontSize: "14px", color: "var(--dark-11)" }}>
            Business Objects
          </div>
        )}
        {this.renderParameterMappings(true)}

        {this.props.parameters
          .filter((p) => p.name !== "workflowStepInstanceId")
          .filter((p) => !(p.isOptionalParameter === true && p.isExternalVisible === false))
          .filter((p) => p.name !== "userInteractionId")
          .filter((p) => p.name !== "isCreatedByWizard")
          .filter((p) => this.props.processParameters.find((x) => x.name === p.name)?.isBusinessObject === false)
          .length > 0 && (
          <div style={{ marginTop: "8px", fontWeight: "600", fontSize: "14px", color: "var(--dark-11)" }}>
            Internal Parameters
          </div>
        )}
        {this.renderParameterMappings(false)}

        {this.renderOutComeMessage()}

        <div className="kuika_switch_property kuika_dont-trigger-validations" style={{ height: 52, paddingTop: 20 }}>
          <div style={{ float: "left" }}>
            <label
              style={{
                width: 174,
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                display: "inline-block"
              }}
            >
              Go Back After Completion
            </label>
          </div>
          <div style={{ float: "right" }}>
            <KuikaSwitch
              onChange={this.props.updateAddGoBackAction}
              checked={this.props.addGoBackActionValue !== false}
              labelPosition="none"
            />
          </div>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const wFResumeSymbolsGroup = withContext<IWFResumeSymbolsGroupProps>(
  withSD<IWFResumeSymbolsGroupProps>(WFResumeSymbolsGroup)
);
export { wFResumeSymbolsGroup as WFResumeSymbolsGroup };
